import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, Trash2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import useCurrentUser from "@/hooks/use-current-user"
import { superAdminCouncilApi } from "@/lib/api"
import type {
  CouncilDetailDTO,
  CouncilSelfDestructStatusDTO,
  UpdateCouncilRequest,
} from "@/gen/api"
import {
  ADMIN_PERMISSION_OBJECTS,
  AdminPermissionObject,
  hasPermission,
} from "@/lib/admin-permissions"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"

type WaterBureauDetailWithInheritance = CouncilDetailDTO & {
  inheritedPermissionObjects?: AdminPermissionObject[]
}

type UpdateCouncilRequestWithInheritance = UpdateCouncilRequest & {
  inheritedPermissionObjects?: AdminPermissionObject[]
}

type WaterBureauSettingsForm = {
  name: string
  description: string
  backendUrl: string
  environment: string
  suspended: boolean
  inheritedPermissionObjects: AdminPermissionObject[]
}

type SelfDestructStatus = "DELETE_REQUESTED" | "DELETING" | "DELETED" | "DELETE_FAILED"

type SelfDestructStatusResponse = CouncilSelfDestructStatusDTO & {
  deletionStatus?: SelfDestructStatus
}

const environments = ["dev", "test", "prod"]
const selfDestructConfirmation = "DELETE WATER BUREAU"
const deletionLockedStatuses: SelfDestructStatus[] = ["DELETE_REQUESTED", "DELETING", "DELETED"]

const normalizeSelfDestructStatus = (status?: string | null): SelfDestructStatus | null => {
  if (!status || status === "NONE") return null
  return status as SelfDestructStatus
}

const getSelfDestructDisplay = (status: SelfDestructStatus) => {
  switch (status) {
    case "DELETE_REQUESTED":
      return {
        title: "Deletion is queued",
        description:
          "This Water Bureau is waiting to be deleted. Settings are locked so no one changes data during the process.",
      }
    case "DELETING":
      return {
        title: "Deletion is in progress",
        description:
          "We are deleting this Water Bureau now. This can take a short time. Settings are locked until it finishes.",
      }
    case "DELETED":
      return {
        title: "Water Bureau deleted",
        description:
          "Deletion is complete. The Water Bureau data has been removed and this settings page is locked.",
      }
    case "DELETE_FAILED":
      return {
        title: "Deletion needs attention",
        description:
          "Deletion did not finish. Check the message below, fix the issue, then try Delete Water Bureau again.",
      }
  }
}

function SettingsLoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-80 max-w-full" />
          </div>
          <Skeleton className="h-6 w-11 rounded-full" />
        </div>
      </div>
      <div className="rounded-lg border p-4">
        <div className="mb-4 space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {["dashboard", "admin", "waterbureau", "devices", "customers", "efficiency"].map((key) => (
            <div key={key} className="flex items-center gap-2">
              <Skeleton className="size-4" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between gap-3">
        <Skeleton className="h-10 w-40" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-28" />
        </div>
      </div>
    </div>
  )
}

export default function WaterbureauSettingsPage() {
  const navigate = useNavigate()
  const { councilId } = useParams()
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const { toast } = useToast()
  const isSuperAdmin = hasRole("super_admin")
  const canWriteWaterBureaus =
    isSuperAdmin ||
    hasPermission(user?.permissions, "water_bureaus", "write_own") ||
    hasPermission(user?.permissions, "water_bureaus", "write_all")
  const canSelfDestruct =
    isSuperAdmin || hasPermission(user?.permissions, "water_bureaus", "write_all")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState("")
  const [selfDestructStatus, setSelfDestructStatus] = useState<SelfDestructStatus | null>(null)
  const [selfDestructFailure, setSelfDestructFailure] = useState<string | null>(null)
  const [form, setForm] = useState<WaterBureauSettingsForm>({
    name: "",
    description: "",
    backendUrl: "",
    environment: "dev",
    suspended: false,
    inheritedPermissionObjects: [],
  })
  const deletionLocked = selfDestructStatus
    ? deletionLockedStatuses.includes(selfDestructStatus)
    : false
  const deletionInProgress =
    selfDestructStatus === "DELETE_REQUESTED" || selfDestructStatus === "DELETING"
  const selfDestructDisplay = selfDestructStatus
    ? getSelfDestructDisplay(selfDestructStatus)
    : null

  const loadSelfDestructStatus = useCallback(async () => {
    if (!councilId || !canSelfDestruct) return
    const statusResponse = await superAdminCouncilApi.getSelfDestructStatus(councilId)
    const status = statusResponse.data.data as SelfDestructStatusResponse | undefined
    setSelfDestructStatus(normalizeSelfDestructStatus(status?.deletionStatus))
    setSelfDestructFailure(status?.deletionStatusMessage ?? null)
  }, [canSelfDestruct, councilId])

  useEffect(() => {
    if (!councilId || !canWriteWaterBureaus) return

    const loadSettings = async () => {
      setIsLoading(true)
      try {
        const response = await superAdminCouncilApi.getCouncil(councilId)
        const council = response.data.data as WaterBureauDetailWithInheritance | undefined
        if (!council) return
        setForm({
          name: council.name ?? "",
          description: council.description ?? "",
          backendUrl: council.backendUrl ?? "",
          environment: council.environment?.toLowerCase() ?? "dev",
          suspended: council.status?.toLowerCase() === "suspended",
          inheritedPermissionObjects: council.inheritedPermissionObjects ?? [],
        })
        await loadSelfDestructStatus()
      } catch {
        toast({
          title: "Error",
          description: "Failed to load Water Bureau settings",
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadSettings()
  }, [canWriteWaterBureaus, councilId, loadSelfDestructStatus, toast])

  // Redirect away from deleted Water Bureaus — settings page must not be accessible.
  useEffect(() => {
    if (selfDestructStatus !== "DELETED") return
    toast({ title: "Access denied", description: "This Water Bureau has been deleted.", variant: "destructive" })
    const t = setTimeout(() => navigate("/manage-waterbureau", { replace: true }), 0)
    return () => clearTimeout(t)
  }, [selfDestructStatus, navigate, toast])

  useEffect(() => {
    if (!deletionInProgress) return
    const intervalId = window.setInterval(() => {
      void loadSelfDestructStatus()
    }, 5000)
    return () => window.clearInterval(intervalId)
  }, [deletionInProgress, loadSelfDestructStatus])

  const saveSettings = async () => {
    if (!councilId || deletionLocked) return
    setIsSaving(true)
    try {
      const payload: UpdateCouncilRequestWithInheritance = {
        name: form.name,
        description: form.description,
        backendUrl: form.backendUrl,
        environment: form.environment.toUpperCase(),
        status: form.suspended ? "SUSPENDED" : "ACTIVE",
        inheritedPermissionObjects: form.inheritedPermissionObjects,
      }
      await superAdminCouncilApi.updateCouncil(councilId, payload)
      toast({ title: "Saved", description: "Water Bureau settings updated" })
      navigate("/manage-waterbureau")
    } catch {
      toast({
        title: "Error",
        description: "Failed to save Water Bureau settings",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const deleteWaterBureau = async () => {
    if (!councilId || deleteConfirmation !== selfDestructConfirmation) return
    setIsDeleting(true)
    try {
      const response = await superAdminCouncilApi.requestSelfDestruct(councilId, {
        confirmation: selfDestructConfirmation,
      })
      const status = response.data.data as SelfDestructStatusResponse | undefined
      setSelfDestructStatus(normalizeSelfDestructStatus(status?.deletionStatus) ?? "DELETING")
      setSelfDestructFailure(status?.deletionStatusMessage ?? null)
      setDeleteOpen(false)
      setDeleteConfirmation("")
      toast({
        title: "Self-destruct requested",
          description: `${form.name || "Water Bureau"} deletion has started.`,
      })
    } catch {
      toast({
        title: "Error",
        description: "Failed to request Water Bureau self-destruct",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  if (!canWriteWaterBureaus) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-xl font-semibold">Access denied</h2>
        <p className="mt-2 text-muted-foreground">
          You need Water Bureaus write permission to update this page.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/manage-waterbureau")}>
        <ArrowLeft className="mr-2 size-4" /> Back to Water Bureaus
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Water Bureau Settings</CardTitle>
          <p className="text-sm text-muted-foreground">
            Update Water Bureau details, access status, environment, and permission inheritance.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
            {selfDestructDisplay ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
                <p className="font-medium">{selfDestructDisplay.title}</p>
                <p className="mt-1 text-muted-foreground">{selfDestructDisplay.description}</p>
                {selfDestructFailure ? (
                  <p className={selfDestructStatus === "DELETE_FAILED" ? "mt-2 text-destructive" : "mt-2 text-muted-foreground"}>
                    {selfDestructStatus === "DELETE_FAILED" ? `What happened: ${selfDestructFailure}` : selfDestructFailure}
                  </p>
                ) : null}
            </div>
          ) : null}
          {isLoading ? (
            <SettingsLoadingSkeleton />
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="wb-settings-name">Name</Label>
                  <Input
                    id="wb-settings-name"
                    value={form.name}
                    disabled={deletionLocked}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, name: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wb-settings-environment">Environment</Label>
                  <Select
                    value={form.environment}
                    disabled={deletionLocked}
                    onValueChange={(value) =>
                      setForm((current) => ({ ...current, environment: value }))
                    }
                  >
                    <SelectTrigger id="wb-settings-environment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {environments.map((environment) => (
                        <SelectItem key={environment} value={environment}>
                          {environment.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wb-settings-description">Description</Label>
                <Input
                  id="wb-settings-description"
                  value={form.description}
                  disabled={deletionLocked}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, description: event.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wb-settings-backend-url">Base URL</Label>
                <Input
                  id="wb-settings-backend-url"
                  value={form.backendUrl}
                  disabled={deletionLocked}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, backendUrl: event.target.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label>Suspend Access</Label>
                  <p className="text-sm text-muted-foreground">
                    Leave untoggled to keep access active. Toggle on to suspend non-super-admin
                    access.
                  </p>
                </div>
                <Switch
                  checked={form.suspended}
                  disabled={deletionLocked}
                  onCheckedChange={(checked) =>
                    setForm((current) => ({ ...current, suspended: checked }))
                  }
                />
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div>
                  <Label>Permissions Inheritance</Label>
                  <p className="text-sm text-muted-foreground">
                    Select which N3 admin permission types inherit into this Water Bureau. Super
                    admins ignore inheritance and inherit all permissions.
                  </p>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {ADMIN_PERMISSION_OBJECTS.map((permission) => {
                    const id = `settings-inherit-${permission.object}`
                    return (
                      <div key={permission.object} className="flex items-center gap-2">
                        <Checkbox
                          id={id}
                          checked={form.inheritedPermissionObjects.includes(permission.object)}
                          disabled={deletionLocked}
                          onCheckedChange={(checked) =>
                            setForm((current) => ({
                              ...current,
                              inheritedPermissionObjects:
                                checked === true
                                  ? [
                                      ...new Set([
                                        ...current.inheritedPermissionObjects,
                                        permission.object,
                                      ]),
                                    ]
                                  : current.inheritedPermissionObjects.filter(
                                      (object) => object !== permission.object,
                                    ),
                            }))
                          }
                        />
                        <Label htmlFor={id}>{permission.label}</Label>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteOpen(true)}
                    disabled={!canSelfDestruct || deletionLocked}
                  >
                    <Trash2 className="mr-2 size-4" /> Delete Water Bureau
                  </Button>
                  {!canSelfDestruct ? (
                    <p className="text-xs text-muted-foreground">
                      Requires global Water Bureaus write permission.
                    </p>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigate("/manage-waterbureau")}>
                    Cancel
                  </Button>
                  <Button
                    onClick={saveSettings}
                    disabled={isSaving || deletionLocked || !form.name || !form.backendUrl}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {form.name || "Water Bureau"}?</AlertDialogTitle>
            <AlertDialogDescription>
                This permanently deletes this Water Bureau and its related admins, end users,
                devices, readings, alerts, and reports. You cannot undo this after it starts. Type{" "}
                {selfDestructConfirmation}
                {" "}to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="wb-self-destruct-confirmation">Confirmation</Label>
            <Input
              id="wb-self-destruct-confirmation"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              placeholder={selfDestructConfirmation}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteWaterBureau}
              disabled={isDeleting || deleteConfirmation !== selfDestructConfirmation}
            >
              {isDeleting ? "Requesting..." : "Delete Water Bureau"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
